import { createMuiTheme } from "@material-ui/core/styles";

/**
 * Définition des thèmes Material-UI (v4) pour Cockpit.
 *
 * - `lightTheme` : thème clair.
 * - `darkTheme` : thème sombre + overrides de composants.
 *
 * @typedef {import("@material-ui/core/styles").Theme} MuiTheme
 */

/**
 * Couleur de fond principale en mode sombre.
 * Référence visuelle : rgb(26, 32, 53)
 * @type {string}
 */
const darkBackground = "#1a2035";

/**
 * Couleur de fond "paper" (cartes, panneaux) en mode sombre.
 * @type {string}
 */
const darkBackgroundPaper = "#1f2940";

/**
 * Couleur de fond par défaut (body) en mode sombre.
 * @type {string}
 */
const darkBackgroundDefault = "#1a2035";

/**
 * Couleur de fond principale en mode clair.
 * @type {string}
 */
const lightBackground = "#ffffff";

/**
 * Couleur de fond "paper" (cartes, panneaux) en mode clair.
 * @type {string}
 */
const lightBackgroundPaper = "#ffffff";

/**
 * Couleur de fond par défaut (body) en mode clair.
 * @type {string}
 */
const lightBackgroundDefault = "#f5f5f5";

/**
 * Couleur primaire commune (clair/sombre).
 * @type {string}
 */
const primaryColor = "#3f51b5";

/**
 * Couleur secondaire commune (clair/sombre).
 * @type {string}
 */
const secondaryColor = "#ff0000";

/**
 * Thème Material-UI clair.
 * @type {MuiTheme}
 */
export const lightTheme = createMuiTheme({
	palette: {
		type: "light",
		primary: {
			main: primaryColor,
			light: "#26c6da",
			dark: "#0097a7",
			contrastText: "#fff"
		},
		secondary: {
			main: secondaryColor,
			light: "#9c27b0",
			dark: "#6a1b9a",
			contrastText: "#fff"
		},
		background: {
			default: lightBackgroundDefault,
			paper: lightBackgroundPaper
		},
		text: {
			primary: "rgba(0, 0, 0, 0.87)",
			secondary: "rgba(0, 0, 0, 0.54)",
			disabled: "rgba(0, 0, 0, 0.38)",
			hint: "rgba(0, 0, 0, 0.38)"
		},
		divider: "rgba(0, 0, 0, 0.12)"
	},
	typography: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
	}
});

/**
 * Thème Material-UI sombre.
 * @type {MuiTheme}
 */
export const darkTheme = createMuiTheme({
	palette: {
		type: "dark",
		primary: {
			main: primaryColor,
			light: "#26c6da",
			dark: "#0097a7",
			contrastText: "#fff"
		},
		secondary: {
			main: secondaryColor,
			light: "#9c27b0",
			dark: "#6a1b9a",
			contrastText: "#fff"
		},
		background: {
			default: darkBackgroundDefault,
			paper: darkBackgroundPaper
		},
		text: {
			primary: "#ffffff",
			secondary: "rgba(255, 255, 255, 0.7)",
			disabled: "rgba(255, 255, 255, 0.5)",
			hint: "rgba(255, 255, 255, 0.5)"
		},
		divider: "rgba(255, 255, 255, 0.12)",
		action: {
			active: "#ffffff",
			hover: "rgba(255, 255, 255, 0.08)",
			selected: "rgba(255, 255, 255, 0.16)",
			disabled: "rgba(255, 255, 255, 0.3)",
			disabledBackground: "rgba(255, 255, 255, 0.12)"
		}
	},
	typography: {
		fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
	},
	overrides: {
		MuiCssBaseline: {
			"@global": {
				body: {
					backgroundColor: darkBackgroundDefault,
					color: "#ffffff"
				}
			}
		},
		MuiPaper: {
			root: {
				backgroundColor: darkBackgroundPaper
			}
		},
		MuiCard: {
			root: {
				backgroundColor: darkBackgroundPaper
			}
		},
		MuiDrawer: {
			paper: {
				backgroundColor: darkBackground
			}
		},
		MuiDialog: {
			paper: {
				backgroundColor: darkBackgroundPaper
			}
		},
		MuiMenu: {
			paper: {
				backgroundColor: darkBackgroundPaper
			}
		},
		MuiAccordion: {
			root: {
				backgroundColor: darkBackgroundPaper,
				"&:before": {
					backgroundColor: "rgba(255, 255, 255, 0.12)"
				}
			}
		},
		MuiTableCell: {
			root: {
				borderBottomColor: "rgba(255, 255, 255, 0.12)"
			},
			head: {
				color: "#ffffff"
			},
			body: {
				color: "rgba(255, 255, 255, 0.87)"
			}
		},
		MuiInputBase: {
			root: {
				color: "#ffffff"
			}
		},
		MuiOutlinedInput: {
			notchedOutline: {
				borderColor: "rgba(255, 255, 255, 0.23)"
			}
		},
		MuiInputLabel: {
			root: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiFormLabel: {
			root: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiSelect: {
			icon: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiListItem: {
			root: {
				"&$selected": {
					backgroundColor: "rgba(255, 255, 255, 0.16)"
				}
			}
		},
		MuiListItemText: {
			primary: {
				color: "#ffffff"
			},
			secondary: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiTab: {
			root: {
				color: "rgba(255, 255, 255, 0.7)",
				"&$selected": {
					color: "#ffffff"
				}
			}
		},
		MuiIconButton: {
			root: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiChip: {
			root: {
				backgroundColor: "rgba(255, 255, 255, 0.16)",
				color: "#ffffff"
			}
		},
		MuiDivider: {
			root: {
				backgroundColor: "rgba(255, 255, 255, 0.12)"
			}
		},
		MuiSwitch: {
			track: {
				backgroundColor: "rgba(255, 255, 255, 0.3)"
			}
		},
		MuiCheckbox: {
			root: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		},
		MuiRadio: {
			root: {
				color: "rgba(255, 255, 255, 0.7)"
			}
		}
	}
});

/**
 * Exports groupés (pratique pour certains imports).
 * @type {{ lightTheme: MuiTheme, darkTheme: MuiTheme }}
 */
export default { lightTheme, darkTheme };
